import React, { useMemo, useState } from 'react';
import { Checkbox } from '@repo/common';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { JOB_LABEL, isAssigneeOf, isLeader, projectDeadline, visibleTasks } from './model/derive';
import { PastDueWarning } from './TaskActionModals';
import { formatDue, fromInputValue, toInputValue, tzShortName } from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import { Field, Modal, ModalButton, Select, TextInput } from './Modal';
import DateTimeField from './DateTimeField';
import TdButton from './Button';

const HALF_HOUR = 30 * 60 * 1000;
const ALL_JOBS = ['planner', 'designer', 'developer', 'marketer'];
const JOB_PLURAL = {
  planner: 'PM',
  designer: 'Designers',
  developer: 'Developers',
  marketer: 'Marketers',
};
/** "Developers and PM" */
export const jobsText = (jobs) => {
  const names = ALL_JOBS.filter((j) => jobs.includes(j)).map((j) => JOB_PLURAL[j]);
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * 예상 제출 시각 고르기 — 지금 값에서 30분 단위로 늦추기만 (프로젝트 종료까지).
 * 아직 정하지 않았으면 지금 이후 첫 30분 칸부터.
 */
const expectedOptions = (current, now, end) => {
  const from = current ? current.getTime() : Math.ceil(now.getTime() / HALF_HOUR) * HALF_HOUR;
  const out = [];
  for (let t = from; t <= end.getTime(); t += HALF_HOUR) out.push(t);
  return out;
};

/**
 * 체크인 폼 — 진행 한 줄 + 진행 중인 내 태스크의 예상 제출 시각 (늦추기만).
 * 체크인은 팀 전체에 공개되고, 제출·수정할 때마다 리더에게 알림이 간다 (수정 횟수 제한 없음).
 * 출석 판정은 처음 체크인한 시각 기준이라 나중에 고쳐도 지각이 되지 않는다.
 * My check-ins 의 카드·행 안에 펼쳐서 쓴다 (모달 아님).
 */
export const CheckInForm = ({ checkpoint, onDone, onCancel }) => {
  const ctx = useWorkboardCtx();
  const { data, dispatch, now, displayTz } = ctx;
  const tz = tzShortName(displayTz, now);
  // 프로젝트 마감 뒤 시각은 고를 수 없다
  const end = projectDeadline(data.project);
  const mine = (data.checkins ?? []).find(
    (c) => c.checkpointId === checkpoint.id && c.userId === data.viewerId,
  );
  const tasks = useMemo(
    () =>
      visibleTasks(data.tasks).filter(
        (t) =>
          isAssigneeOf(t, data.viewerId) &&
          (!t.acceptedById || t.acceptedById === data.viewerId) &&
          ['in_progress', 'rework_requested'].includes(t.state),
      ),
    [data.tasks, data.viewerId],
  );
  const [note, setNote] = useState(mine?.note ?? '');
  const leaderName = data.members.find(isLeader)?.name;
  const notified = `${leaderName ? `${leaderName} (leader)` : 'Your leader'} gets notified`;
  const late = !mine && now > new Date(checkpoint.scheduledAt);
  const [times, setTimes] = useState({}); // taskId → ms (바꾼 것만)
  const ok = !!note.trim() && !ctx.actionsLocked;

  const submit = () => {
    dispatch({
      type: 'checkIn',
      checkpointId: checkpoint.id,
      note: note.trim(),
      expected: Object.entries(times).map(([taskId, ms]) => ({
        taskId,
        value: new Date(ms).toISOString(),
      })),
      at: now.toISOString(),
    });
    ctx.toast?.(
      mine
        ? { title: 'Check-in updated', sub: notified }
        : late
          ? { title: 'Checked in late', sub: `${checkpoint.name} · ${notified}` }
          : { title: 'Checked in · On time', sub: notified },
    );
    onDone?.();
  };

  return (
    <div className='flex flex-col gap-4 rounded-xl bg-green-deep03/[0.03] p-4'>
      <Field label='Progress' required htmlFor={`checkin-note-${checkpoint.id}`}>
        <TextInput
          id={`checkin-note-${checkpoint.id}`}
          autoFocus
          placeholder='One line on where you are'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && ok && submit()}
        />
      </Field>

      {tasks.length > 0 && (
        <div className='flex flex-col gap-2.5'>
          <span className='text-[13px] font-medium text-text-primary'>
            My tasks in progress · expected submit ({tz})
          </span>
          <div className='flex gap-2 rounded-lg border border-green-deep03/[0.12] bg-grayscale-gray00 px-3 py-2.5 text-[12px] leading-[150%] text-text-secondary'>
            <InfoIcon className='mt-0.5 h-4 w-4 shrink-0 text-icon-secondary' />
            <span>
              Running late? Update this at least 30 min before the due time. Only your first update
              per task counts as an early notice. Later changes or missed times can lower your
              score.
            </span>
          </div>
          <ul className='flex flex-col gap-2'>
            {tasks.map((t) => {
              const current = t.expectedSubmitAt ? new Date(t.expectedSubmitAt) : null;
              const { effective } = dueInfo(ctx, t);
              const options = expectedOptions(current, now, end);
              return (
                <li
                  key={t.id}
                  className='flex flex-wrap items-center gap-3 rounded-lg border border-green-deep03/[0.12] bg-grayscale-gray00 px-3 py-2.5'
                >
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                      {t.title}
                    </span>
                    <span className='text-[12px] text-text-secondary'>
                      Due {formatDue(effective, displayTz)} {tz}
                    </span>
                  </span>
                  <Select
                    aria-label={`Expected submit time for ${t.title}`}
                    value={times[t.id] ?? current?.getTime() ?? ''}
                    onChange={(e) => {
                      const ms = Number(e.target.value);
                      setTimes((m) => {
                        const next = { ...m };
                        if (!ms || ms === current?.getTime()) delete next[t.id];
                        else next[t.id] = ms;
                        return next;
                      });
                    }}
                    className='w-[180px] shrink-0 text-[13px] sm:w-[200px]'
                  >
                    {!current && <option value=''>Not set</option>}
                    {options.map((ms) => (
                      <option key={ms} value={ms}>
                        {formatDue(new Date(ms), displayTz)} {tz}
                        {ms > effective.getTime() ? ' · after due' : ''}
                      </option>
                    ))}
                  </Select>
                  {times[t.id] > effective.getTime() && (
                    <div className='basis-full'>
                      <PastDueWarning due={effective} displayTz={displayTz} tz={tz} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className='flex items-center justify-end gap-2'>
        {!ok && (
          <span className='mr-auto text-[13px] text-text-secondary'>
            Write one line of progress to check in
          </span>
        )}
        {onCancel && (
          <TdButton variant='ghost' onClick={onCancel} className='px-4'>
            Cancel
          </TdButton>
        )}
        <ModalButton primary disabled={!ok} onClick={submit}>
          {mine ? 'Save changes' : late ? 'Check in late' : 'Check in'}
        </ModalButton>
      </div>
    </div>
  );
};

/**
 * general 체크포인트 만들기·수정 (리더).
 * 대상은 직군 단위. 수정하면 대상자에게 알림이 간다.
 */
export const GeneralCheckpointModal = ({ checkpoint, onClose }) => {
  const { data, dispatch, now, displayTz, toast } = useWorkboardCtx();
  const editing = !!checkpoint;
  // 직군 4개는 늘 보인다 (Task Flows W6_AddCheckpointModal)
  const jobs = ALL_JOBS;
  const tz = tzShortName(displayTz, now);
  const [name, setName] = useState(checkpoint?.name ?? '');
  const [time, setTime] = useState(
    checkpoint ? toInputValue(new Date(checkpoint.scheduledAt), displayTz) : '',
  );
  const [targets, setTargets] = useState(checkpoint?.targetJobs ?? []);
  const at = fromInputValue(time, displayTz);
  // 체크포인트도 프로젝트 마감까지만
  const end = projectDeadline(data.project);
  const timeOk = at && at > now && at <= end;
  const changed =
    !editing ||
    name.trim() !== checkpoint.name ||
    at?.toISOString() !== checkpoint.scheduledAt ||
    [...targets].sort().join() !== [...checkpoint.targetJobs].sort().join();
  const ok = name.trim() && timeOk && targets.length && changed;

  const confirm = () => {
    if (editing) {
      dispatch({
        type: 'editCheckpoint',
        checkpointId: checkpoint.id,
        patch: { name: name.trim(), scheduledAt: at.toISOString(), targetJobs: targets },
        at: now.toISOString(),
      });
    } else {
      dispatch({
        type: 'addCheckpoint',
        checkpoint: {
          id: `g-${Date.now()}`,
          type: 'general',
          name: name.trim(),
          scheduledAt: at.toISOString(),
          createdBy: data.viewerId,
          targetJobs: targets,
        },
        at: now.toISOString(),
      });
    }
    toast?.(
      editing
        ? { title: 'Checkpoint updated', sub: `${name.trim()} · ${jobsText(targets)} get notified` }
        : { title: 'Checkpoint added', sub: `${name.trim()} · ${jobsText(targets)}` },
    );
    onClose();
  };

  return (
    <Modal
      title={editing ? 'Edit checkpoint' : 'Add checkpoint'}
      subtitle={`General · ${data.project.teamName ?? ''}`}
      onClose={onClose}
      footer={
        <>
          {!ok && !editing && (
            <span className='mr-auto text-[13px] text-text-secondary'>
              Add a name, time and at least one job
            </span>
          )}
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton primary disabled={!ok} onClick={confirm}>
            {editing ? 'Save changes' : 'Add checkpoint'}
          </ModalButton>
        </>
      }
    >
      <Field label='Name' required htmlFor='cp-name'>
        <TextInput
          id='cp-name'
          autoFocus
          placeholder='e.g. Design review'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label={`Time (${tz})`} required htmlFor='cp-time'>
        <DateTimeField
          id='cp-time'
          value={time}
          min={toInputValue(now, displayTz)}
          max={toInputValue(end, displayTz)}
          onChange={setTime}
        />
      </Field>
      <fieldset className='flex flex-col gap-2'>
        <legend className='mb-1 text-[13px] font-medium text-text-primary'>
          For<span className='text-functional-failed03'>*</span>
        </legend>
        {jobs.map((job) => (
          <Checkbox
            key={job}
            checked={targets.includes(job)}
            onChange={(e) =>
              setTargets((t) => (e.target.checked ? [...t, job] : t.filter((x) => x !== job)))
            }
            label={JOB_LABEL[job]}
            labelClassName='text-[14px] text-text-primary'
          />
        ))}
        {editing && (
          <span className='text-[12px] text-text-secondary'>
            Saving notifies everyone it applies to.
          </span>
        )}
      </fieldset>
    </Modal>
  );
};
